import type { LinkMapPage } from '../../types';

/**
 * Facts about a page and a link that more than one surface needs.
 *
 * Formerly `linkFacts`, which built the rows of a tree that no longer
 * exists. What survived its deletion is everything that was never about trees:
 * whether a link will ship, where it came from, and how dangerous a removal is.
 *
 * Dependency-free on purpose. CRA's jest cannot load `d3-force`, so anything
 * the site graph imports and a test needs has to live outside those components
 * — which is what makes these testable at all.
 */

/**
 * What a drop CREATES: one link, or a mutual pair.
 *
 * Lives here rather than in the workspace that owns the state, because the
 * dock's toolbar renders the control and the workspace persists the choice — a
 * second local copy of the union is how one of them ends up with a third value
 * nothing handles.
 */
export type DropMode = 'one-way' | 'both';

/**
 * How useful is this page ON THIS SCREEN, lowest number first.
 *
 * Not alphabetical, and not by id — both scatter the handful of pages you can
 * act on through a list of ones you cannot. Against real data the first
 * actionable page sat at row 13 of 59, below twelve rows titled "Page 60".
 * The dock's columns sort by this.
 *
 *   0  written and live      — can be linked from AND to
 *   1  written, not live      — can be linked from; links TO it are skipped
 *   2  not written            — inert: the AI is never offered it, and it
 *                               cannot deploy, so nothing can link to it
 */
export function actionability(page: LinkMapPage): number {
  if (!page.is_written) return 2;
  return page.is_published ? 0 : 1;
}

/**
 * Why a link to this page would not ship, or null if it would.
 *
 * Shown on the row itself rather than discovered at deploy. Both cases produce
 * the same invisible failure — the edge exists in the map, is drawn on screen,
 * and is silently dropped when the page is published.
 */
export function unlinkableReason(page: LinkMapPage): string | null {
  // Order matters. An unwritten page is ALSO unpublished, and reporting it as
  // "never published" sends the user to the deploy screen for a page that has
  // nothing to deploy. The first true statement is the useful one.
  if (!page.is_written) {
    return 'Not written yet - build this page before linking it';
  }
  if (!page.is_published) {
    return 'Never published - links to this page are skipped until it deploys';
  }
  if (!page.public_path) {
    return 'No address yet - set this site’s public URL so other pages can link here';
  }
  return null;
}

/**
 * Where an edge came from, and whether Relink will keep it.
 *
 * THREE STATES OUT OF TWO FIELDS, which is why this is a function and not a
 * column. `generated_at` says whether a human or the AI drew it; `locked` says
 * whether the next relink may replace it. The combination is what the user
 * actually needs, and computing it in two places is how the two disagree.
 *
 * Lived in `EdgePane` until the Tree was retired. Moved here rather than
 * deleted with it: the distinction is the reason anyone dares press Relink.
 */
export function provenanceOf(edge: {
  generated_at: string | null;
  locked: boolean;
}): { label: string; className: string; title: string } {
  if (!edge.generated_at) {
    return {
      label: 'yours',
      className: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
      title: 'You drew this link. Relink never touches it.',
    };
  }
  if (edge.locked) {
    return {
      label: 'AI · kept',
      className: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
      title: 'The AI drew this and you locked it. Relink will not replace it.',
    };
  }
  return {
    label: 'AI',
    className: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
    title: 'The AI drew this and it is unlocked - the next Relink may clear it.',
  };
}

/** A two-or-three word version of the above, for a row. */
export function stateLabel(page: LinkMapPage): string | null {
  if (!page.is_written) return 'not written';
  if (!page.is_published) return 'not live';
  if (!page.public_path) return 'no address';
  return null;
}

/**
 * What removing a source costs, in the words shown before it happens.
 *
 * Lives here because there are TWO ways to remove one — the × on a source chip
 * and the Remove button in the scope picker — and a warning that names an
 * edge count is exactly the kind of text that drifts between two copies until
 * one of them stops saying the dangerous part. Removing a source deletes every
 * link with an end outside what remains, hand-drawn and locked ones included.
 */
export function removeScopeConfirm(
  label: string,
  edgeCount: number,
  fallout?: { disappearing: string[]; shrinking: string[] },
): string {
  const links =
    edgeCount > 0
      ? ` (this map has ${edgeCount} link${edgeCount === 1 ? '' : 's'})`
      : '';

  // WHAT HAPPENS TO THE SITES, before the click rather than after. A subject
  // that feeds two sites does not necessarily take either with it, and a node
  // that correctly survives its subject reads as a refresh that failed.
  const sites: string[] = [];
  if (fallout?.disappearing.length) {
    sites.push(
      `${fallout.disappearing.join(', ')} will disappear from the map - ` +
        `no other subject has pages there.`,
    );
  }
  if (fallout?.shrinking.length) {
    sites.push(
      `${fallout.shrinking.join(', ')} will stay, with fewer pages - ` +
        `other subjects also publish there.`,
    );
  }

  return (
    `Remove "${label}" from this map?\n\n` +
    'Its pages leave the map, and any links between them and the rest of it ' +
    `map are deleted${links}.` +
    (sites.length ? `\n\n${sites.join('\n\n')}` : '') +
    '\n\nContinue?'
  );
}
